import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Plus, 
  Eye, 
  AlertTriangle,
  Activity,
  FileText,
  Download,
  Search,
  Filter,
  Calendar
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { inventoryService } from '../../services/inventoryService';
import { productService } from '../../services/productService';
import { printService } from '../../services/printService';
import { cartService } from '../../services/cartService';
import { InventorySession, StockAlert, StockMovement } from '../../types';

function MagasinsList() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [activeTab, setActiveTab] = useState<'inventory' | 'alerts' | 'activity'>('inventory');
  const [inventorySessions, setInventorySessions] = useState<InventorySession[]>([]);
  const [stockAlerts, setStockAlerts] = useState<StockAlert[]>([]);
  const [stockActivity, setStockActivity] = useState<StockMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    productId: '',
    userId: '',
    type: ''
  });

  useEffect(() => {
    loadData();
  }, [activeTab, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      switch (activeTab) {
        case 'inventory':
          const sessions = await inventoryService.getInventorySessions();
          setInventorySessions(sessions);
          break;
        case 'alerts':
          const alerts = await inventoryService.getStockAlerts();
          setStockAlerts(alerts);
          break;
        case 'activity':
          const activity = await inventoryService.getStockActivity(filters);
          setStockActivity(activity);
          break;
      }
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les données',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSupplierOrder = async (alerts: StockAlert[]) => {
    // Créer une commande fournisseur avec les produits en alerte
    addNotification({
      userId: user?.id || '',
      title: 'Commande créée',
      message: `Commande fournisseur créée pour ${alerts.length} produit(s)`,
      type: 'success'
    });
  };

  const handleExportActivity = async () => {
    try {
      const data = stockActivity.map(movement => ({
        Date: new Date(movement.createdAt).toLocaleString('fr-FR'),
        Type: inventoryService.getMovementTypeLabel(movement.type),
        Produit: movement.productId,
        Quantité: movement.quantity,
        Raison: movement.reason,
        Utilisateur: movement.userId
      }));
      
      await printService.exportCSV(data, 'activite_stock');
      
      addNotification({
        userId: user?.id || '',
        title: 'Export réussi',
        message: 'L\'activité des stocks a été exportée',
        type: 'success'
      });
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur d\'export',
        message: 'Impossible d\'exporter les données',
        type: 'error'
      });
    }
  };

  const canManageInventory = user?.role === 'responsable_depot' || user?.role === 'responsable_bureau';
  const canCreateInventory = canManageInventory || user?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-teal-500 to-teal-700 rounded-lg shadow-lg">
                <Package className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Magasins</h1>
            </div>
            
            {canCreateInventory && activeTab === 'inventory' && (
              <Link
                to="/magasins/inventory/new"
                className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Nouvel inventaire</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('inventory')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'inventory'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Inventaires ({inventorySessions.length})
            </button>
            <button
              onClick={() => setActiveTab('alerts')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'alerts'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Stock de sécurité ({stockAlerts.length})
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'activity'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Activité des stocks
            </button>
          </div>
        </div>

        {/* Filters for Activity */}
        {activeTab === 'activity' && (
          <div className="px-6 py-4 border-b border-gray-200/50">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white/80"
                placeholder="Date début"
              />
              
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white/80"
                placeholder="Date fin"
              />
              
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white/80"
              >
                <option value="">Tous les types</option>
                <option value="entry">Entrée</option>
                <option value="exit">Sortie</option>
                <option value="adjustment">Ajustement</option>
                <option value="transfer">Transfert</option>
              </select>
              
              <button
                onClick={() => setFilters({ startDate: '', endDate: '', productId: '', userId: '', type: '' })}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white/80 hover:bg-white transition-colors"
              >
                Réinitialiser
              </button>
              
              <button
                onClick={handleExportActivity}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Export CSV</span>
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="min-h-96">
          {activeTab === 'inventory' && (
            inventorySessions.length === 0 ? (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun inventaire</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Créez votre premier inventaire
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Inventaire
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Scope
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Articles
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white/50 divide-y divide-gray-200">
                    {inventorySessions.map((session) => (
                      <tr key={session.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {session.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            #{session.id.slice(-6)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {session.scope === 'all' ? 'Complet' : 
                           session.scope === 'category' ? 'Catégorie' : 'Emplacement'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            inventoryService.getStatusColor(session.status)
                          }`}>
                            {inventoryService.getStatusLabel(session.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {session.items.length} articles
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(session.createdAt).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/magasins/inventory/${session.id}`}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="Voir les détails"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {activeTab === 'alerts' && (
            <div className="p-6">
              {stockAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune alerte stock</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Tous les produits sont au-dessus du seuil minimum
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-gray-900">
                      Produits en alerte ({stockAlerts.length})
                    </h3>
                    
                    {canManageInventory && (
                      <button
                        onClick={() => handleCreateSupplierOrder(stockAlerts)}
                        className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Créer commande fournisseur</span>
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {stockAlerts.map((alert) => (
                      <div key={alert.productId} className="bg-white rounded-lg border border-red-200 p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center">
                            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                            <span className="text-sm font-medium text-gray-900">
                              {productService.getProductName ? productService.getProductName(alert.productId) : alert.productId}
                            </span>
                          </div>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Stock actuel:</span>
                            <span className="font-medium text-red-600">{alert.currentStock}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Seuil minimum:</span>
                            <span className="font-medium">{alert.minThreshold}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Quantité suggérée:</span>
                            <span className="font-medium text-green-600">{alert.suggestedQuantity}</span>
                          </div>
                        </div>
                        
                        <div className="mt-4 flex space-x-2">
                          <Link
                            to={`/products/${alert.productId}`}
                            className="flex-1 text-center px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                          >
                            Détails
                          </Link>
                          <button className="flex-1 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors">
                            Commander
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            stockActivity.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune activité</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Aucun mouvement ne correspond aux filtres
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Produit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantité
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Raison
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Utilisateur
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white/50 divide-y divide-gray-200">
                    {stockActivity.map((movement) => (
                      <tr key={movement.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(movement.createdAt).toLocaleString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            inventoryService.getMovementTypeColor(movement.type)
                          }`}>
                            {inventoryService.getMovementTypeLabel(movement.type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {movement.productId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-medium ${
                            movement.quantity > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {movement.reason}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {movement.userId}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default MagasinsList;